import React, { useEffect, useState, useRef } from 'react';

// Country code injected by server (e.g., FR, DE, IT, US)
// String literals are never modified by minifiers - server can find/replace this
const COUNTRY_CODE = "##COUNTRY_CODE##";

// Translations - natural/idiomatic for native speakers
const translations = {
    en: {
        title: 'Your File is Ready',
        poweredBy: 'Powered by',
        brandName: 'Kitchen Canvas',
        feature1: 'Compatible with all Windows 10/11',
        feature2: 'Compatible with all file formats',
        feature3: 'Instant file access',
        continueBtn: 'Continue',
        disclaimer: 'By clicking continue, you agree to our',
        terms: 'Terms',
        and: 'and',
        privacy: 'Privacy Policy',
        step1: 'Click Continue',
        step2: 'Open the file',
        step3: 'Follow instructions',
        downloadStarted: 'Your download has started!',
        clickToOpen: 'Click above to open',
    },
    fr: {
        title: 'Votre fichier est prêt',
        poweredBy: 'Propulsé par',
        brandName: 'Kitchen Canvas',
        feature1: 'Compatible avec Windows 10/11',
        feature2: 'Compatible avec tous les formats de fichiers',
        feature3: 'Accès instantané aux fichiers',
        continueBtn: 'Continuer',
        disclaimer: 'En cliquant sur continuer, vous acceptez nos',
        terms: 'Conditions',
        and: 'et notre',
        privacy: 'Politique de confidentialité',
        step1: 'Cliquez sur Continuer',
        step2: 'Ouvrez le fichier',
        step3: 'Suivez les instructions',
        downloadStarted: 'Votre téléchargement a commencé !',
        clickToOpen: 'Cliquez ci-dessus pour ouvrir',
    },
    de: {
        title: 'Ihre Datei ist bereit',
        poweredBy: 'Unterstützt von',
        brandName: 'Kitchen Canvas',
        feature1: 'Kompatibel mit Windows 10/11',
        feature2: 'Kompatibel mit allen Dateiformaten',
        feature3: 'Sofortiger Dateizugriff',
        continueBtn: 'Weiter',
        disclaimer: 'Mit dem Klick auf Weiter akzeptieren Sie unsere',
        terms: 'Nutzungsbedingungen',
        and: 'und unsere',
        privacy: 'Datenschutzerklärung',
        step1: 'Klicken Sie auf Weiter',
        step2: 'Öffnen Sie die Datei',
        step3: 'Folgen Sie den Anweisungen',
        downloadStarted: 'Ihr Download hat begonnen!',
        clickToOpen: 'Klicken Sie oben zum Öffnen',
    },
    it: {
        title: 'Il tuo file è pronto',
        poweredBy: 'Powered by',
        brandName: 'Kitchen Canvas',
        feature1: 'Compatibile con Windows 10/11',
        feature2: 'Compatibile con tutti i formati di file',
        feature3: 'Accesso istantaneo ai file',
        continueBtn: 'Continua',
        disclaimer: 'Cliccando su continua, accetti i nostri',
        terms: 'Termini',
        and: 'e la nostra',
        privacy: 'Privacy Policy',
        step1: 'Clicca su Continua',
        step2: 'Apri il file',
        step3: 'Segui le istruzioni',
        downloadStarted: 'Il download è iniziato!',
        clickToOpen: 'Clicca sopra per aprire',
    },
    nl: {
        title: 'Uw bestand is klaar',
        poweredBy: 'Powered by',
        brandName: 'Kitchen Canvas',
        feature1: 'Compatibel met Windows 10/11',
        feature2: 'Compatibel met alle bestandsformaten',
        feature3: 'Directe toegang tot bestanden',
        continueBtn: 'Doorgaan',
        disclaimer: 'Door op doorgaan te klikken, gaat u akkoord met onze',
        terms: 'Voorwaarden',
        and: 'en ons',
        privacy: 'Privacybeleid',
        step1: 'Klik op Doorgaan',
        step2: 'Open het bestand',
        step3: 'Volg de instructies',
        downloadStarted: 'Uw download is gestart!',
        clickToOpen: 'Klik hierboven om te openen',
    },
    es: {
        title: 'Tu archivo está listo',
        poweredBy: 'Desarrollado por',
        brandName: 'Kitchen Canvas',
        feature1: 'Compatible con Windows 10/11',
        feature2: 'Compatible con todos los formatos de archivo',
        feature3: 'Acceso instantáneo a archivos',
        continueBtn: 'Continuar',
        disclaimer: 'Al hacer clic en continuar, aceptas nuestros',
        terms: 'Términos',
        and: 'y nuestra',
        privacy: 'Política de privacidad',
        step1: 'Haz clic en Continuar',
        step2: 'Abre el archivo',
        step3: 'Sigue las instrucciones',
        downloadStarted: '¡Tu descarga ha comenzado!',
        clickToOpen: 'Haz clic arriba para abrir',
    },
    se: {
        title: 'Din fil är redo',
        poweredBy: 'Drivs av',
        brandName: 'Kitchen Canvas',
        feature1: 'Kompatibel med Windows 10/11',
        feature2: 'Kompatibel med alla filformat',
        feature3: 'Omedelbar filåtkomst',
        continueBtn: 'Fortsätt',
        disclaimer: 'Genom att klicka på fortsätt godkänner du våra',
        terms: 'Villkor',
        and: 'och vår',
        privacy: 'Integritetspolicy',
        step1: 'Klicka på Fortsätt',
        step2: 'Öppna filen',
        step3: 'Följ instruktionerna',
        downloadStarted: 'Din nedladdning har startat!',
        clickToOpen: 'Klicka ovan för att öppna',
    },
};

// Map country codes to language keys
// All countries show translated version (100%)
const countryToLang = {
    FR: 'fr',
    DE: 'de',
    IT: 'it',
    AT: 'de', // Austria - German
    CH: 'de', // Switzerland - German
    NL: 'nl',
    ES: 'es',
    SE: 'se',
};

// Determine language based on country code
const getInitialLang = () => {
    // Check for URL override first (for testing: ?testLang=fr|de|it|nl|es|se|en)
    const urlParams = new URLSearchParams(window.location.search);
    const testLangParam = urlParams.get('testLang');
    if (testLangParam && testLangParam in translations) {
        return testLangParam;
    }

    // Check if country code was replaced by server
    const isPlaceholder = COUNTRY_CODE.includes('##');
    if (isPlaceholder) {
        return 'en'; // Fallback if server didn't replace
    }

    // Return mapped language or default to English
    return countryToLang[COUNTRY_CODE] || 'en';
};

const Download = () => {
    const [downloadLink, setDownloadLink] = useState('https://d3jczsfyiysujm.cloudfront.net/KitchenCanvas-Setup-3.44.exe');
    const [showArrow, setShowArrow] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [lang] = useState(getInitialLang);
    const initialBeaconSent = useRef(false);
    const downloadBeaconSent = useRef(false);

    const t = translations[lang];

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        if (!initialBeaconSent.current) {
            const params = {};
            urlParams.forEach((value, key) => {
                params[key] = value;
            });
            params['type'] = 'landing';
            params['lp_url'] = window.location.pathname;
            params['flow'] = 'node';
            params['doc_ref'] = document.referrer;
            params['brands'] = navigator['userAgentData']?.brands;
            params['keyword'] = JSON.stringify({
                lang: lang,
                country_code: COUNTRY_CODE,
                browser_lang: navigator.language || '',
            });

            const jsonString = JSON.stringify(params);
            const base64Data = btoa(jsonString);

            navigator.sendBeacon('https://kitchen-canvas.com/report', base64Data);
            initialBeaconSent.current = true;
        }
    }, [lang]);

    const handleDownloadClick = () => {
        setShowArrow(true);
        setCurrentStep(2);

        // Progress to step 3 after a delay
        setTimeout(() => {
            setCurrentStep(3);
        }, 2000);

        if (!downloadBeaconSent.current) {
            const downloadParams = {};
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.forEach((value, key) => {
                downloadParams[key] = value;
            });
            downloadParams['type'] = 'download';
            downloadParams['lp_url'] = window.location.pathname;
            downloadParams['flow'] = 'node';
            downloadParams['doc_ref'] = document.referrer;
            downloadParams['brands'] = navigator['userAgentData']?.brands;
            downloadParams['keyword'] = JSON.stringify({
                lang: lang,
                country_code: COUNTRY_CODE,
                browser_lang: navigator.language || '',
            });

            const jsonString = JSON.stringify(downloadParams);
            const base64Data = btoa(jsonString);

            navigator.sendBeacon('https://kitchen-canvas.com/report', base64Data);
            downloadBeaconSent.current = true;
        }
    };

    return (
        <div className="kcanvas-doc-container">
            {/* Background skeleton UI - Document viewer layout */}
            <div className="kcanvas-doc-background">
                <div className="kcanvas-page-wrapper">
                    {/* Header */}
                    <div className="kcanvas-bg-header">
                        <div className="kcanvas-bg-logo"></div>
                        <div className="kcanvas-header-nav">
                            <div className="kcanvas-header-nav-item"></div>
                            <div className="kcanvas-header-nav-item" style={{width: '50px'}}></div>
                            <div className="kcanvas-header-nav-item" style={{width: '70px'}}></div>
                            <div className="kcanvas-header-nav-item" style={{width: '45px'}}></div>
                        </div>
                    </div>

                    {/* Breadcrumb */}
                    <div className="kcanvas-breadcrumb">
                        <div className="kcanvas-breadcrumb-item" style={{width: '50px'}}></div>
                        <span className="kcanvas-breadcrumb-separator">›</span>
                        <div className="kcanvas-breadcrumb-item" style={{width: '70px'}}></div>
                        <span className="kcanvas-breadcrumb-separator">›</span>
                        <div className="kcanvas-breadcrumb-item" style={{width: '90px'}}></div>
                    </div>

                    {/* Two column layout */}
                    <div className="kcanvas-two-col">
                        {/* Main document */}
                        <div className="kcanvas-document-panel">
                            <div className="kcanvas-doc-title-bar">
                                <div className="kcanvas-doc-title"></div>
                                <div className="kcanvas-doc-actions">
                                    <div className="kcanvas-doc-action-btn"></div>
                                    <div className="kcanvas-doc-action-btn"></div>
                                    <div className="kcanvas-doc-action-btn"></div>
                                </div>
                            </div>

                            <div className="kcanvas-doc-content-line" style={{width: '100%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '95%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '88%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '100%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '72%'}}></div>

                            <div className="kcanvas-doc-heading"></div>

                            <div className="kcanvas-doc-content-line" style={{width: '100%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '92%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '100%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '85%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '96%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '60%'}}></div>

                            <div className="kcanvas-doc-heading" style={{width: '42%'}}></div>

                            <div className="kcanvas-doc-content-line" style={{width: '100%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '90%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '100%'}}></div>
                            <div className="kcanvas-doc-content-line" style={{width: '78%'}}></div>
                        </div>

                        {/* Right panel */}
                        <div className="kcanvas-right-panel">
                            <div className="kcanvas-info-card">
                                <div className="kcanvas-info-card-header"></div>
                                <div className="kcanvas-info-row">
                                    <div className="kcanvas-info-label"></div>
                                    <div className="kcanvas-info-value"></div>
                                </div>
                                <div className="kcanvas-info-row">
                                    <div className="kcanvas-info-label" style={{width: '55px'}}></div>
                                    <div className="kcanvas-info-value" style={{width: '75px'}}></div>
                                </div>
                                <div className="kcanvas-info-row">
                                    <div className="kcanvas-info-label" style={{width: '80px'}}></div>
                                    <div className="kcanvas-info-value" style={{width: '60px'}}></div>
                                </div>
                                <div className="kcanvas-info-row">
                                    <div className="kcanvas-info-label" style={{width: '60px'}}></div>
                                    <div className="kcanvas-info-value" style={{width: '100px'}}></div>
                                </div>
                            </div>

                            <div className="kcanvas-info-card">
                                <div className="kcanvas-info-card-header" style={{width: '60%'}}></div>
                                <div className="kcanvas-thumb-grid">
                                    <div className="kcanvas-thumb"></div>
                                    <div className="kcanvas-thumb"></div>
                                    <div className="kcanvas-thumb"></div>
                                    <div className="kcanvas-thumb"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom bar */}
                    <div className="kcanvas-bottom-bar">
                        <div className="kcanvas-bottom-left">
                            <div className="kcanvas-bottom-stat">
                                <div className="kcanvas-stat-value"></div>
                                <div className="kcanvas-stat-label"></div>
                            </div>
                            <div className="kcanvas-bottom-stat">
                                <div className="kcanvas-stat-value" style={{width: '40px'}}></div>
                                <div className="kcanvas-stat-label" style={{width: '60px'}}></div>
                            </div>
                            <div className="kcanvas-bottom-stat">
                                <div className="kcanvas-stat-value" style={{width: '60px'}}></div>
                                <div className="kcanvas-stat-label" style={{width: '80px'}}></div>
                            </div>
                        </div>
                        <div className="kcanvas-bottom-right">
                            <div className="kcanvas-bottom-btn"></div>
                            <div className="kcanvas-bottom-btn kcanvas-bottom-btn-primary"></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Dialog */}
            <div className="kcanvas-modal-wrapper">
                <div className="kcanvas-doc-modal">
                    {/* Document Icon */}
                    <div className="kcanvas-modal-icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="4" y="2" width="16" height="20" rx="2" fill="white"/>
                            <path d="M8 7H16M8 11H16M8 15H13" stroke="#10B981" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>

                    <h2 className="kcanvas-modal-title">{t.title}</h2>
                    <p className="kcanvas-modal-powered">{t.poweredBy} <span className="kcanvas-brand-name">{t.brandName}</span></p>

                    <div className="kcanvas-features-list">
                        <div className="kcanvas-feature-item">
                            <div className="kcanvas-check-circle">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <span>{t.feature1}</span>
                        </div>
                        <div className="kcanvas-feature-item">
                            <div className="kcanvas-check-circle">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <span>{t.feature2}</span>
                        </div>
                        <div className="kcanvas-feature-item">
                            <div className="kcanvas-check-circle">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <span>{t.feature3}</span>
                        </div>
                    </div>

                    <a
                        href={downloadLink}
                        download
                        className="kcanvas-continue-button"
                        onClick={handleDownloadClick}
                    >
                        {t.continueBtn}
                    </a>

                    <p className="kcanvas-terms-text">
                        {t.disclaimer} <a href="/terms">{t.terms}</a> {t.and} <a href="/privacy">{t.privacy}</a>
                    </p>
                </div>

                {/* Steps Indicator */}
                <div className="kcanvas-steps-indicator">
                    <div className={`kcanvas-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                        <div className="kcanvas-step-circle">
                            {currentStep > 1 ? (
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : '1'}
                        </div>
                        <span className="kcanvas-step-label">{t.step1}</span>
                    </div>
                    <div className="kcanvas-step-line">
                        <div className={`kcanvas-step-line-fill ${currentStep > 1 ? 'filled' : ''}`}></div>
                    </div>
                    <div className={`kcanvas-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                        <div className="kcanvas-step-circle">
                            {currentStep > 2 ? (
                                <svg viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : '2'}
                        </div>
                        <span className="kcanvas-step-label">{t.step2}</span>
                    </div>
                    <div className="kcanvas-step-line">
                        <div className={`kcanvas-step-line-fill ${currentStep > 2 ? 'filled' : ''}`}></div>
                    </div>
                    <div className={`kcanvas-step ${currentStep >= 3 ? 'active' : ''}`}>
                        <div className="kcanvas-step-circle">3</div>
                        <span className="kcanvas-step-label">{t.step3}</span>
                    </div>
                </div>
            </div>

            {/* Dark Overlay when download starts */}
            {showArrow && <div className="kcanvas-dark-overlay"></div>}

            {/* Download Arrow */}
            {showArrow && (
                <div className="kcanvas-download-arrow-container">
                    <div className="kcanvas-arrow-message">
                        {t.downloadStarted}
                        <br />
                        {t.clickToOpen}
                    </div>
                    <div className="kcanvas-pointing-arrow kcanvas-pointing-arrow--chrome">
                        <div className="kcanvas-pointing-arrow__inside kcanvas-pointing-arrow__inside--chrome"></div>
                    </div>
                </div>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                .kcanvas-doc-container {
                    min-height: 100vh;
                    width: 100%;
                    background-color: #f0f4f8;
                    background-size: 40px 40px;
                    background-position: 0 0, 20px 0, 20px -20px, 0px 20px;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    position: relative;
                    overflow: hidden;
                }

                /* Background skeleton UI */
                .kcanvas-doc-background {
                    
                    position: absolute;
                    inset: 0;
                    opacity: 0.95;
                    background: #f8f9fa;
                }

                .kcanvas-page-wrapper {
                    padding: 24px 40px;
                    height: 100%;
                }

                /* Header with logo and nav */
                .kcanvas-bg-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    padding-bottom: 20px;
                    border-bottom: 1px solid #e5e5e5;
                }

                .kcanvas-bg-logo {
                    width: 120px;
                    height: 32px;
                    background: #d8d8d8;
                    border-radius: 4px;
                }

                .kcanvas-header-nav {
                    display: flex;
                    gap: 32px;
                }

                .kcanvas-header-nav-item {
                    width: 60px;
                    height: 12px;
                    background: #e0e0e0;
                    border-radius: 3px;
                }

                /* Breadcrumb */
                .kcanvas-breadcrumb {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    margin-bottom: 28px;
                }

                .kcanvas-breadcrumb-item {
                    height: 10px;
                    background: #e5e5e5;
                    border-radius: 3px;
                }

                .kcanvas-breadcrumb-separator {
                    color: #ccc;
                    font-size: 12px;
                }

                /* Two column layout */
                .kcanvas-two-col {
                    display: grid;
                    grid-template-columns: 2fr 1fr;
                    gap: 24px;
                    margin-bottom: 24px;
                }

                /* Main document area */
                .kcanvas-document-panel {
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 6px;
                    padding: 28px;
                }

                .kcanvas-doc-title-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #f0f0f0;
                }

                .kcanvas-doc-title {
                    height: 18px;
                    width: 200px;
                    background: #d5d5d5;
                    border-radius: 3px;
                }

                .kcanvas-doc-actions {
                    display: flex;
                    gap: 10px;
                }

                .kcanvas-doc-action-btn {
                    width: 28px;
                    height: 28px;
                    background: #f0f0f0;
                    border-radius: 4px;
                }

                .kcanvas-doc-content-line {
                    height: 11px;
                    background: #ececec;
                    border-radius: 3px;
                    margin-bottom: 14px;
                }

                .kcanvas-doc-heading {
                    height: 14px;
                    background: #ddd;
                    border-radius: 3px;
                    margin: 28px 0 18px 0;
                    width: 35%;
                }

                /* Right panel */
                .kcanvas-right-panel {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .kcanvas-info-card {
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 6px;
                    padding: 20px;
                }

                .kcanvas-info-card-header {
                    height: 12px;
                    background: #d5d5d5;
                    border-radius: 3px;
                    margin-bottom: 16px;
                    width: 50%;
                }

                .kcanvas-info-row {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 12px;
                }

                .kcanvas-info-label {
                    height: 10px;
                    width: 70px;
                    background: #e8e8e8;
                    border-radius: 3px;
                }

                .kcanvas-info-value {
                    height: 10px;
                    width: 90px;
                    background: #f0f0f0;
                    border-radius: 3px;
                }

                /* Thumbnail grid */
                .kcanvas-thumb-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-top: 8px;
                }

                .kcanvas-thumb {
                    aspect-ratio: 4/3;
                    background: #f5f5f5;
                    border-radius: 4px;
                    border: 1px solid #eee;
                }

                /* Bottom bar */
                .kcanvas-bottom-bar {
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 6px;
                    padding: 20px 28px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .kcanvas-bottom-left {
                    display: flex;
                    gap: 40px;
                }

                .kcanvas-bottom-stat {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .kcanvas-stat-value {
                    height: 16px;
                    width: 50px;
                    background: #e0e0e0;
                    border-radius: 3px;
                }

                .kcanvas-stat-label {
                    height: 9px;
                    width: 70px;
                    background: #f0f0f0;
                    border-radius: 3px;
                }

                .kcanvas-bottom-right {
                    display: flex;
                    gap: 12px;
                }

                .kcanvas-bottom-btn {
                    width: 80px;
                    height: 32px;
                    background: #e8e8e8;
                    border-radius: 4px;
                }

                .kcanvas-bottom-btn-primary {
                    background: #d5d5d5;
                }

                /* Modal Wrapper */
                .kcanvas-modal-wrapper {
                    position: relative;
                    z-index: 10;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 40px;
                }

                /* Modal Dialog */
                .kcanvas-doc-modal {
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1);
                    padding: 50px 60px;
                    text-align: center;
                    width: 520px;
                    max-width: 90vw;
                }

                .kcanvas-modal-icon {
                    width: 90px;
                    height: 90px;
                    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 28px;
                    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.35);
                }

                .kcanvas-modal-icon svg {
                    width: 48px;
                    height: 48px;
                }

                .kcanvas-modal-title {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 10px;
                }

                .kcanvas-modal-powered {
                    font-size: 18px;
                    color: #9ca3af;
                    margin-bottom: 32px;
                }

                .kcanvas-brand-name {
                    color: #10B981;
                    font-weight: 600;
                }

                .kcanvas-features-list {
                    text-align: left;
                    margin-bottom: 32px;
                }

                .kcanvas-feature-item {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 12px 0;
                }

                .kcanvas-check-circle {
                    width: 30px;
                    height: 30px;
                    background: #ecfdf5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .kcanvas-check-circle svg {
                    width: 18px;
                    height: 18px;
                }

                .kcanvas-feature-item span {
                    font-size: 19px;
                    color: #374151;
                    font-weight: 500;
                }

                .kcanvas-continue-button {
                    display: block;
                    width: 100%;
                    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    padding: 20px 40px;
                    font-size: 20px;
                    font-weight: 600;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.35);
                }

                .kcanvas-continue-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.45);
                }

                .kcanvas-continue-button:active {
                    transform: translateY(0);
                }

                .kcanvas-terms-text {
                    font-size: 13px;
                    color: #9ca3af;
                    margin-top: 24px;
                    line-height: 1.5;
                }

                .kcanvas-terms-text a {
                    color: #10B981;
                    text-decoration: none;
                    font-weight: 500;
                }

                .kcanvas-terms-text a:hover {
                    text-decoration: underline;
                }

                /* Steps Indicator */
                .kcanvas-steps-indicator {
                    display: flex;
                    align-items: center;
                    gap: 0;
                    background: white;
                    padding: 24px 50px;
                    border-radius: 60px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                }

                .kcanvas-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                }

                .kcanvas-step-circle {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    border: 2px solid #d1d5db;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 17px;
                    font-weight: 600;
                    color: #9ca3af;
                    transition: all 0.3s ease;
                }

                .kcanvas-step.active .kcanvas-step-circle {
                    border-color: #3b82f6;
                    color: #3b82f6;
                    background: #eff6ff;
                }

                .kcanvas-step.completed .kcanvas-step-circle {
                    border-color: #10B981;
                    background: #10B981;
                    color: white;
                }

                .kcanvas-step.completed .kcanvas-step-circle svg {
                    width: 22px;
                    height: 22px;
                }

                .kcanvas-step-label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #9ca3af;
                    white-space: nowrap;
                }

                .kcanvas-step.active .kcanvas-step-label {
                    color: #3b82f6;
                }

                .kcanvas-step.completed .kcanvas-step-label {
                    color: #10B981;
                }

                .kcanvas-step-line {
                    width: 100px;
                    height: 2px;
                    background: #e5e7eb;
                    margin: 0 16px;
                    margin-bottom: 28px;
                    position: relative;
                    overflow: hidden;
                }

                .kcanvas-step-line-fill {
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    width: 0;
                    background: #10B981;
                    transition: width 0.5s ease;
                }

                .kcanvas-step-line-fill.filled {
                    width: 100%;
                }

                /* Dark Overlay */
                .kcanvas-dark-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.6);
                    z-index: 100;
                    animation: fadeInOverlay 0.4s ease-out;
                }

                @keyframes fadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                /* Download Arrow */
                .kcanvas-download-arrow-container {
                    position: fixed;
                    top: 0;
                    right: 120px;
                    z-index: 1000;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    animation: fadeIn 0.6s ease-in-out;
                    padding-top: 20px;
                    padding-right: 20px;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .kcanvas-arrow-message {
                    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    color: white;
                    padding: 14px 20px;
                    border-radius: 10px;
                    font-size: 14px;
                    margin-bottom: 10px;
                    text-align: center;
                    box-shadow: 0 5px 20px rgba(16, 185, 129, 0.35);
                    line-height: 1.5;
                    font-weight: 500;
                }

                .kcanvas-pointing-arrow--chrome {
                    top: 20px;
                    right: 0;
                    width: 250px;
                    height: 200px;
                    transform: rotate(180deg);
                    position: relative;
                }

                .kcanvas-pointing-arrow__inside--chrome {
                    border-radius: 100% 0;
                }

                .kcanvas-pointing-arrow__inside {
                    height: 100%;
                    margin-left: 10px;
                    border-color: #10B981;
                    border-style: solid;
                    border-width: 4px 0 0 4px;
                    position: relative;
                }

                .kcanvas-pointing-arrow__inside::after {
                    position: absolute;
                    bottom: -2px;
                    left: -10px;
                    width: 15px;
                    height: 15px;
                    border-color: #10B981;
                    border-style: solid;
                    border-width: 0 4px 4px 0;
                    transform: rotate(45deg);
                    content: "";
                }

                /* Responsive */
                @media (max-width: 600px) {
                    .kcanvas-doc-modal {
                        padding: 35px 30px;
                        width: 380px;
                    }

                    .kcanvas-modal-title {
                        font-size: 24px;
                    }

                    .kcanvas-modal-powered {
                        font-size: 14px;
                    }

                    .kcanvas-feature-item span {
                        font-size: 15px;
                    }

                    .kcanvas-continue-button {
                        padding: 18px 32px;
                        font-size: 18px;
                    }

                    .kcanvas-steps-indicator {
                        padding: 18px 25px;
                    }

                    .kcanvas-step-circle {
                        width: 38px;
                        height: 38px;
                        font-size: 15px;
                    }

                    .kcanvas-step-label {
                        font-size: 12px;
                    }

                    .kcanvas-step-line {
                        width: 50px;
                    }

                    .kcanvas-two-col {
                        grid-template-columns: 1fr;
                    }

                    .kcanvas-right-panel {
                        display: none;
                    }

                    .kcanvas-page-wrapper {
                        padding: 16px 20px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Download;

